import { Action, createReducer, on } from '@ngrx/store';
import { Photo } from '@classifieds-sell/trademe-core-ui';
import {
    Metadata,
    ValidateListingResponse,
    ValidationResult
} from 'libs/datalayer/src';
import { ErrorModel } from '../common.models';
import { ListingActionResponseModel } from './models/listing-action-response.model';
import * as DraftActions from './draft.actions';
import { PropertyAgentContactDetailsType } from 'libs/property-ui/src/lib/models/property-agent-contact-details/property-agent-contact-details-model';

export const DRAFT_FEATURE_KEY = 'draft';

export interface DraftState {
    id: number;
    categoryId: number;
    status: number;

    originalDraftData: any;
    draftData: any;
    validationSchema: any;
    metadata: Metadata;
    validationResults: ValidationResult[];
    validateListingResponse: ValidateListingResponse;

    makeListingActionLoading?: boolean;
    makeListingActionResponse: ListingActionResponseModel;

    draftLoaded: boolean;

    isLoadingDraftSuccess?: boolean;

    // temporary solution for loading status (for saving draft)
    // these will eventually be moved to `Loading State` to be able to keep track of each action?
    // and for GetValidation and GetDraftData as well
    isLoading?: boolean;
    loadingSuccess?: boolean;
    loadingFailure?: boolean;
    error?: ErrorModel | null;
}

export interface DraftPartialState {
    readonly [DRAFT_FEATURE_KEY]: DraftState;
}

export const draftInitialState: DraftState = {
    id: 0,
    categoryId: 0,
    status: 0,

    originalDraftData: null,
    draftData: null,
    validationSchema: null,
    metadata: {} as Metadata,
    validationResults: [],
    validateListingResponse: null,

    makeListingActionLoading: false,
    makeListingActionResponse: null,

    draftLoaded: false,

    isLoading: false,
    loadingSuccess: false,
    loadingFailure: false
};

const draftReducer = createReducer(
    draftInitialState,
    on(DraftActions.getDraftSuccess, (state, { draft }) => ({
        ...state,
        ...draft,
        originalDraftData: draft.originalDraftData ? { ...draft.originalDraftData } : { ...draft.draftData },
        draftLoaded: true,
        isLoadingDraftSuccess: true
    })),
    on(DraftActions.getDraftFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        isLoadingDraftSuccess: false,
        error
    })),
    on(DraftActions.updateDraftData, (state, { draftData }) => ({
        ...state,
        draftData: draftData
    })),
    on(DraftActions.updateValidationResult, (state, { validationResult }) => ({
        ...state,
        validationResults:
            state.validationResults === null
                ? [validationResult]
                : state.validationResults.some(
                      (x) => x.stepId === validationResult.stepId
                  )
                ? state.validationResults.map((val) => {
                      // update the existing validation step
                      if (val.stepId === validationResult.stepId) {
                          return validationResult;
                      }

                      return val;
                  })
                : [...state.validationResults, validationResult]
    })),
    on(DraftActions.saveDraft, (state) => ({
        ...state,
        isLoading: true,
        loadingSuccess: false,
        loadingFailure: false
    })),
    on(DraftActions.saveDraftSuccess, (state, { result, lastSavedStepId }) => ({
        ...state,
        isLoading: false,
        loadingSuccess: true,
        draftData: {
            ...state.draftData,
            lastSavedStepId
        },
        id: result.draftId,
        status: result.draftStatus
    })),
    on(DraftActions.saveDraftFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        loadingFailure: true,
        error
    })),
    on(DraftActions.resetSaveDraftLoadingStatus, (state) => ({
        ...state,
        loadingSuccess: false,
        loadingFailure: false
    })),
    on(DraftActions.makeListingAction, (state) => ({
        ...state,
        makeListingActionLoading: true
    })),
    on(
        DraftActions.makeListingActionSuccess,
        (state, { listingRequest, listingRequestType }) => ({
            ...state,
            makeListingActionResponse: {
                response: listingRequest,
                type: listingRequestType
            },
            makeListingActionLoading: false
        })
    ),
    on(
        DraftActions.makeListingActionFailure,
        (state, { error, listingRequestType }) => ({
            ...state,
            makeListingActionLoading: false,
            makeListingActionResponse: {
                response: {
                    success: false,
                    description: error?.message
                },
                type: listingRequestType
            },
            error
        })
    ),
    on(DraftActions.validateListingSuccess, (state, { validListing }) => ({
        ...state,
        validateListingResponse: validListing
    })),
    on(DraftActions.validateListingFailure, (state, { error }) => ({
        ...state,
        validateListingResponse: {
            success: false,
            description: error?.message
        },
        error
    })),
    on(
        DraftActions.addPhotoToDraftData,
        (state, { photo, parentProperty }) => ({
            ...state,
            draftData: {
                ...state.draftData,
                [parentProperty]: {
                    ...state.draftData[parentProperty],
                    photos: [
                        ...(state.draftData[parentProperty].photos || []),
                        photo
                    ]
                }
            }
        })
    ),
    on(
        DraftActions.addAgentPhotoToDraftData,
        (state, { photo, agenttContactDetailsType, parentProperty }) => ({
            ...state,
            draftData: {
                ...state.draftData,
                [parentProperty]: {
                    ...state.draftData[parentProperty],
                    ...(agenttContactDetailsType === PropertyAgentContactDetailsType.primary && { brandingImageId: photo.photoId }),
                    ...(agenttContactDetailsType === PropertyAgentContactDetailsType.secondary && { secondBrandingImageId: photo.photoId })
                }
            }
        })
    ),
    on(
        DraftActions.removeAgentPhotoFromDraftData,
        (state, { agenttContactDetailsType, parentProperty }) => ({
            ...state,
            draftData: {
                ...state.draftData,
                [parentProperty]: {
                    ...state.draftData[parentProperty],
                    ...(agenttContactDetailsType === PropertyAgentContactDetailsType.primary && { brandingImageId: 0, photoUrl: null }),
                    ...(agenttContactDetailsType === PropertyAgentContactDetailsType.secondary && { secondBrandingImageId: 0, secondPhotoUrl : null })
                }
            }
        })
    ),
    on(
        DraftActions.uploadPhotoSuccess,
        (state, { photoId, photoUrls, parentProperty }) => {
            const photos = [...(state.draftData[parentProperty].photos || [])];

            const index = photos.findIndex((o: Photo) => o.photoId === photoId);
            if (photos[index]) {
                photos[index] = {
                    ...photos[index],
                    ...photoUrls,
                    loading: false
                };
            }
            return {
                ...state,
                draftData: {
                    ...state.draftData,
                    [parentProperty]: {
                        ...state.draftData[parentProperty],
                        photos: [...photos]
                    }
                }
            };
        }
    ),
    on(
        DraftActions.uploadPhotoFailure,
        (state, { photoId, error, parentProperty }) => {
            const photos = [...(state.draftData[parentProperty].photos || [])];

            const index = photos.findIndex((o: Photo) => o.photoId === photoId);
            if (photos[index]) {
                photos[index] = {
                    ...photos[index],
                    loading: false,
                    hasError: true,
                    errorMessage: error.error?.ErrorDescription
                };
            }
            return {
                ...state,
                draftData: {
                    ...state.draftData,
                    [parentProperty]: {
                        ...state.draftData[parentProperty],
                        photos: [...photos]
                    }
                }
            };
        }
    ),
    on(
        DraftActions.uploadAgentPhotoSuccess,
        (state, { photoId, photoUrl, agenttContactDetailsType, parentProperty }) => {
            let newState = {
                ...state,
                draftData: {
                    ...state.draftData,
                    [parentProperty]: {
                        ...state.draftData[parentProperty],
                        ...(agenttContactDetailsType === PropertyAgentContactDetailsType.primary && { photoUrl: photoUrl, brandingImageId: photoId }),
                        ...(agenttContactDetailsType === PropertyAgentContactDetailsType.secondary && { secondPhotoUrl: photoUrl, secondBrandingImageId: photoId })
                    }
                }
            };
            return newState;
        }
    ),
    on(
        DraftActions.uploadAgentPhotoFailure,
        (state, { error, agenttContactDetailsType, parentProperty }) => {
            return {
                ...state,
                draftData: {
                    ...state.draftData,
                    [parentProperty]: {
                        ...state.draftData[parentProperty],
                        ...(agenttContactDetailsType === PropertyAgentContactDetailsType.primary && { photoErrorMessage: error.error?.ErrorDescription }),
                        ...(agenttContactDetailsType === PropertyAgentContactDetailsType.secondary && { secondPhotoErrorMessage: error.error?.ErrorDescription })
                    }
                }
            }
        }
    ),
    on(
        DraftActions.updatePhotosInDraftData,
        (state, { photos, parentProperty }) => ({
            ...state,
            draftData: {
                ...state.draftData,
                [parentProperty]: {
                    ...state.draftData[parentProperty],
                    photos: [...photos]
                }
            }
        })
    ),
    on(
        DraftActions.updateTransformPhotoDataToDraftData,
        (state, { editedPhotoData, parentProperty }) => {
            const photos = [...(state.draftData[parentProperty].photos || [])];

            const index = photos.findIndex(
                (o: Photo) => o.photoId === editedPhotoData.photoId
            );
            if (photos[index]) {
                photos[index] = { ...photos[index], loading: true };
            }
            return {
                ...state,
                draftData: {
                    ...state.draftData,
                    [parentProperty]: {
                        ...state.draftData[parentProperty],
                        photos: [...photos]
                    }
                }
            };
        }
    ),
    on(
        DraftActions.transformPhotoSuccess,
        (state, { photoId, transformedPhoto, crop, parentProperty }) => {
            const photos = [...(state.draftData[parentProperty].photos || [])];

            const index = photos.findIndex((o: Photo) => o.photoId === photoId);
            if (photos[index]) {
                photos[index] = {
                    ...photos[index],
                    transformedPhoto: { ...transformedPhoto, crop: crop },
                    loading: false
                };
            }
            return {
                ...state,
                draftData: {
                    ...state.draftData,
                    [parentProperty]: {
                        ...state.draftData[parentProperty],
                        photos: [...photos]
                    }
                }
            };
        }
    ),
    on(
        DraftActions.transformPhotoFailure,
        (state, { photoId, error, parentProperty }) => {
            const photos = [...(state.draftData[parentProperty].photos || [])];

            const index = photos.findIndex((o: Photo) => o.photoId === photoId);
            if (photos[index]) {
                photos[index] = {
                    ...photos[index],
                    loading: false,
                    hasError: true,
                    errorMessage: error.error?.ErrorDescription
                };
            }
            return {
                ...state,
                draftData: {
                    ...state.draftData,
                    [parentProperty]: {
                        ...state.draftData[parentProperty],
                        photos: [...photos]
                    }
                }
            };
        }
    ),
    on(
        DraftActions.updateInvalidPhoto,
        (state, { photoId, errorMessage, parentProperty }) => {
            const photos = [...(state.draftData[parentProperty].photos || [])];

            const index = photos.findIndex((o: Photo) => o.photoId === photoId);
            if (photos[index]) {
                photos[index] = {
                    ...photos[index],
                    loading: false,
                    hasError: true,
                    errorMessage: errorMessage
                };
            }
            return {
                ...state,
                draftData: {
                    ...state.draftData,
                    [parentProperty]: {
                        ...state.draftData[parentProperty],
                        photos: [...photos]
                    }
                }
            };
        }
    ),
    on(DraftActions.updateCurrentStepSuccess, (state, { currentStepId }) => ({
        ...state,
        draftData: {
            ...state.draftData,
            currentStepId
        }
    })),
    on(
        DraftActions.resetPackageCodeIfNeeded,
        (state, { isRelist, isAgent }) => {
            if (state.draftLoaded) {
                let ret = { ...state };
                if (isRelist) {
                    let promote = {};
                    // we need to reset the packageCode/packageId here so UI
                    // would allow user to select the package again
                    // could be done on server side, but server side is strong typed
                    if (!isAgent) {
                        promote = {
                            ...ret.draftData.propertyPromote,
                            categoryPackageId: null,
                            packageCode: null
                        };
                    } else {
                        promote = {
                            ...ret.draftData.propertyPromote,
                            packageCode: null
                        };
                    }
                    return {
                        ...ret,
                        // spread computed promote object
                        draftData: {
                            ...ret.draftData,
                            propertyPromote: {
                                ...promote
                            }
                        }
                    };
                }
            }
            return state;
        }
    )
);

export function draftReducerFunc(
    state: DraftState | undefined,
    action: Action
) {
    return draftReducer(state, action);
}
